import {
  BenefitsIcon,
  BuildingOutlineIcon,
  CarOutlineIcon,
  CompanyIcon,
  ComplianceIcon,
  ContactIcon,
  CredentialingIcon,
  DataTableIcon,
  DocsIcon,
  FeaturesIcon,
  ForbiddenIcon,
  HandshakeHeartIcon,
  LayoutIcon,
  ListIcon,
  MailIcon,
  MapIcon,
  MinivanIcon,
  OperationsIcon,
  OrderOutlineIcon,
  PersonalIcon,
  PortalInfoIcon,
  PreferencesIcon,
  SearchFileIcon,
  ServicesIcon,
  SettingsIcon,
  SurveyTemplatesIcon,
  UserIcon,
  UsersIcon,
} from '@src/svg';
import MemberReportsIcon from '@src/svg/MemberReportsIcon';
import { idType } from '@src/types';
import { APP_VERSION } from '@src/utils/constants';
import { ReactNode } from 'react';
import { CONTRACTOR_USER } from '@routes/helpers';
import {
  PERMISSION_TITLES,
  TAB_NAME_TITLES,
} from '@hooks/useDefinePermissions';

export type navigationItemType = {
  id: idType;
  href: string;
  title: string;
  module?: string | undefined;
  Icon: ReactNode;
  name?: string;
  children?: Array<navigationItemType>;
};

type navigationDataType = {
  payorsNavigationData: Array<navigationItemType>;
  membersNavigationData: Array<navigationItemType>;
  adminNavigationData: Array<navigationItemType>;
  placesNavigationData: Array<navigationItemType>;
  ridesNavigationData: Array<navigationItemType>;
  monitoringNavigationData: Array<navigationItemType>;
  assignmentNavigationData: Array<navigationItemType>;
  contractorsNavigationData: Array<navigationItemType>;
  standingOrderNavigationData: Array<navigationItemType>;
  reportsNavigationData: Array<navigationItemType>;
  profileNavigationData: Array<navigationItemType>;
};

const auditLogsIcon = (size = 25) => (
  <SearchFileIcon width={size} height={size} />
);

type ItemDef = Omit<navigationItemType, 'id'>;

const item = <T extends Record<string, ItemDef>>(defs: T) => defs;

const buildSection = <T extends Record<string, ItemDef>>(
  defs: T,
  order: Array<keyof T>
): Array<navigationItemType> =>
  order.map((key, index) => ({ id: index + 1, ...defs[key] }));

/*** ============================= PAYORS =================================== ***/

const payorsItems = item({
  infoLight: {
    title: TAB_NAME_TITLES.INFORMATION,
    href: '/information',
    Icon: <BuildingOutlineIcon />,
    name: 'info',
    tabConfigs: [PERMISSION_TITLES.INFO],
  },
  infoFull: {
    title: TAB_NAME_TITLES.INFORMATION,
    href: '/information',
    Icon: <BuildingOutlineIcon />,
    module: 'payor',
    tabConfigs: [PERMISSION_TITLES.INFO],
  },
  features: {
    title: TAB_NAME_TITLES.FEATURES,
    href: '/features',
    Icon: <FeaturesIcon />,
    tabConfigs: [PERMISSION_TITLES.FEATURES],
  },
  services: {
    title: TAB_NAME_TITLES.SERVICES,
    href: '/services',
    Icon: <ServicesIcon />,
    tabConfigs: [PERMISSION_TITLES.LOS, PERMISSION_TITLES.SERVICE_TYPE],
  },
  settings: {
    title: TAB_NAME_TITLES.SETTINGS,
    href: '/settings',
    Icon: <SettingsIcon />,
    tabConfigs: [PERMISSION_TITLES.SETTINGS],
  },
  contacts: {
    title: TAB_NAME_TITLES.CONTACTS,
    href: '/contacts',
    Icon: <ContactIcon />,
    tabConfigs: [PERMISSION_TITLES.AUTH_CONTACTS],
  },
  agreements: {
    title: TAB_NAME_TITLES.AGREEMENTS,
    href: '/agreements',
    Icon: <HandshakeHeartIcon />,
    tabConfigs: [PERMISSION_TITLES.AGREEMENTS],
  },
  documents: {
    title: TAB_NAME_TITLES.DOCUMENTS,
    href: '/documents',
    Icon: <DocsIcon />,
    tabConfigs: [PERMISSION_TITLES.DOCUMENTS],
  },
  access: {
    title: TAB_NAME_TITLES.ACCESS,
    href: '/access',
    Icon: <UsersIcon />,
    tabConfigs: [PERMISSION_TITLES.ACCESS],
  },
  auditLogsSmall: {
    title: TAB_NAME_TITLES.AUDIT_LOGS,
    href: '/audit-logs',
    Icon: auditLogsIcon(24),
    tabConfigs: [
      PERMISSION_TITLES.NOTES,
      PERMISSION_TITLES.MODIFICATION_HISTORY,
      PERMISSION_TITLES.RECORD_VIEW_HISTORY,
    ],
  },
  auditLogs: {
    title: TAB_NAME_TITLES.AUDIT_LOGS,
    href: '/audit-logs',
    Icon: auditLogsIcon(25),
    tabConfigs: [
      PERMISSION_TITLES.NOTES,
      PERMISSION_TITLES.MODIFICATION_HISTORY,
      PERMISSION_TITLES.RECORD_VIEW_HISTORY,
    ],
  },
  exclusions: {
    title: TAB_NAME_TITLES.EXCLUSIONS,
    href: '/exclusions',
    Icon: <ForbiddenIcon width={25} height={25} />,
    tabConfigs: [PERMISSION_TITLES.EXCLUSION],
  },
});

const payorsLightOrder: Array<keyof typeof payorsItems> = [
  'infoLight',
  'features',
  'services',
  'settings',
  'auditLogsSmall',
];
const payorsFullOrder: Array<keyof typeof payorsItems> = [
  'infoFull',
  'contacts',
  'agreements',
  'services',
  'documents',
  'access',
  'settings',
  'auditLogs',
  'exclusions',
];

/*** ============================= MEMBERS =================================== ***/

const membersItems = item({
  infoLight: {
    title: TAB_NAME_TITLES.INFORMATION,
    href: '/information',
    Icon: <BuildingOutlineIcon />,
    name: 'info',
    tabConfigs: [PERMISSION_TITLES.INFO],
  },
  infoFull: {
    title: TAB_NAME_TITLES.INFORMATION,
    href: '/information',
    Icon: <BuildingOutlineIcon />,
    module: 'member',
    tabConfigs: [PERMISSION_TITLES.INFO],
  },
  preferences: {
    title: TAB_NAME_TITLES.PREFERENCES,
    href: '/preferences',
    Icon: <PreferencesIcon />,
    tabConfigs: [PERMISSION_TITLES.PREFERENCES],
  },
  contacts: {
    title: TAB_NAME_TITLES.CONTACTS,
    href: '/contacts',
    Icon: <ContactIcon />,
    tabConfigs: [PERMISSION_TITLES.AUTH_CONTACTS],
  },
  benefits: {
    title: TAB_NAME_TITLES.BENEFITS,
    href: '/benefits',
    Icon: <BenefitsIcon />,
    tabConfigs: [
      PERMISSION_TITLES.ELIGIBILITY_TRANSACTIONS,
      PERMISSION_TITLES.LIMITATIONS,
      PERMISSION_TITLES.SERVICE_AUTHORIZATION,
    ],
  },
  documents: {
    title: TAB_NAME_TITLES.DOCUMENTS,
    href: '/documents',
    Icon: <DocsIcon />,
    tabConfigs: [PERMISSION_TITLES.DOCUMENTS],
  },
  rides: {
    title: TAB_NAME_TITLES.RIDES,
    href: '/rides',
    Icon: <CarOutlineIcon />,
    tabConfigs: [PERMISSION_TITLES.RIDE, PERMISSION_TITLES.STANDING_ORDER],
  },
  auditLogs: {
    title: TAB_NAME_TITLES.AUDIT_LOGS,
    href: '/audit-logs',
    Icon: auditLogsIcon(25),
    tabConfigs: [
      PERMISSION_TITLES.NOTES,
      PERMISSION_TITLES.MODIFICATION_HISTORY,
      PERMISSION_TITLES.RECORD_VIEW_HISTORY,
    ],
  },
});

const membersLightOrder: Array<keyof typeof membersItems> = [
  'infoLight',
  'preferences',
  'contacts',
  'benefits',
  'rides',
  'auditLogs',
];
const membersFullOrder: Array<keyof typeof membersItems> = [
  'infoFull',
  'contacts',
  'benefits',
  'preferences',
  'documents',
  'rides',
  'auditLogs',
];

/*** ============================= HOME ======================================= ***/

const homeItems = item({
  home: {
    title: TAB_NAME_TITLES.HOME,
    href: '',
    Icon: <UsersIcon />,
  },
});
const homeOrder: Array<keyof typeof homeItems> = ['home'];

/*** ============================= ADMIN ======================================= ***/

const adminItems = item({
  usersLight: {
    title: TAB_NAME_TITLES.USERS,
    href: '/users',
    Icon: <UsersIcon />,
    name: 'users',
  },
  usersFull: {
    title: TAB_NAME_TITLES.USERS,
    href: '/users',
    Icon: <UsersIcon />,
  },
  permissionsLight: {
    title: TAB_NAME_TITLES.PERMISSIONS,
    href: '/permissions',
    Icon: <ServicesIcon />,
    name: 'permissions',
  },
  permissionsFull: {
    title: TAB_NAME_TITLES.PERMISSIONS,
    href: '/permissions',
    Icon: <ServicesIcon />,
  },
  surveyTemplates: {
    title: TAB_NAME_TITLES.SURVEY_TEMPLATES,
    href: '/survey-templates',
    Icon: <SurveyTemplatesIcon />,
  },
});
const adminLightOrder: Array<keyof typeof adminItems> = [
  'usersLight',
  'permissionsLight',
];
const adminFullOrder: Array<keyof typeof adminItems> = [
  'usersFull',
  'permissionsFull',
  'surveyTemplates',
];

/*** ============================= PROFILE ===================================== ***/

const profileItems = item({
  userProfile: {
    title: TAB_NAME_TITLES.USER_PROFILE,
    href: '/user-profile',
    Icon: <UsersIcon />,
  },
  auditLogs: {
    title: TAB_NAME_TITLES.AUDIT_LOGS,
    href: '/audit-logs',
    Icon: auditLogsIcon(25),
  },
  recentActivity: {
    title: TAB_NAME_TITLES.RECENT_ACTIVITY,
    href: '/recent-activity',
    Icon: <ListIcon />,
  },
  permissions: {
    title: TAB_NAME_TITLES.PERMISSIONS,
    href: '/permissions',
    Icon: <ServicesIcon />,
  },
});
const profileOrder: Array<keyof typeof profileItems> = [
  'userProfile',
  'auditLogs',
  'recentActivity',
  'permissions',
];

/*** ============================= PLACES ====================================== ***/

const placesItems = item({
  infoLight: {
    title: TAB_NAME_TITLES.INFORMATION,
    href: '/information',
    Icon: <BuildingOutlineIcon />,
    name: 'info',
  },
  infoFull: {
    title: TAB_NAME_TITLES.INFORMATION,
    href: '/information',
    Icon: <BuildingOutlineIcon />,
  },
  contacts: {
    title: TAB_NAME_TITLES.CONTACTS,
    href: '/contacts',
    Icon: <ContactIcon />,
  },
  locationsLight: {
    title: TAB_NAME_TITLES.LOCATIONS,
    href: '/locations',
    Icon: <CompanyIcon />,
    name: 'locations',
  },
  locationsFull: {
    title: TAB_NAME_TITLES.LOCATIONS,
    href: '/locations',
    Icon: <CompanyIcon />,
  },
  services: {
    title: TAB_NAME_TITLES.SERVICES,
    href: '/services',
    Icon: <ServicesIcon />,
  },
  documents: {
    title: TAB_NAME_TITLES.DOCUMENTS,
    href: '/documents',
    Icon: <DocsIcon />,
  },
  access: {
    title: TAB_NAME_TITLES.ACCESS,
    href: '/access',
    Icon: <UsersIcon />,
  },
  rides: {
    title: TAB_NAME_TITLES.RIDES,
    href: '/rides',
    Icon: <CarOutlineIcon />,
  },
  auditLogs: {
    title: TAB_NAME_TITLES.AUDIT_LOGS,
    href: '/audit-logs',
    Icon: auditLogsIcon(25),
    tabConfigs: [
      PERMISSION_TITLES.NOTES,
      PERMISSION_TITLES.MODIFICATION_HISTORY,
      PERMISSION_TITLES.RECORD_VIEW_HISTORY,
    ],
  },
});
const placesLightOrder: Array<keyof typeof placesItems> = [
  'infoLight',
  'contacts',
  'locationsLight',
  'services',
  'documents',
  'access',
  'rides',
  'auditLogs',
];
const placesFullOrder: Array<keyof typeof placesItems> = [
  'infoFull',
  'contacts',
  'locationsFull',
  'services',
  'documents',
  'access',
  'rides',
  'auditLogs',
];

/*** ============================= RIDES ======================================= ***/

const ridesItems = item({
  rideDetailLight: {
    title: TAB_NAME_TITLES.RIDE_DETAIL,
    href: '/information',
    Icon: <CarOutlineIcon />,
    name: 'ride',
    tabConfigs: [PERMISSION_TITLES.INFO],
  },
  rideInfoFull: {
    title: TAB_NAME_TITLES.INFORMATION,
    href: '/information',
    Icon: <CarOutlineIcon />,
    tabConfigs: [PERMISSION_TITLES.INFO],
  },
  standingOrderDetail: {
    title: TAB_NAME_TITLES.STANDING_ORDER_DETAILS,
    href: '/standing-order',
    module: 'standingOrder',
    Icon: <OrderOutlineIcon />,
  },
  contacts: {
    title: TAB_NAME_TITLES.CONTACTS,
    href: '/contacts',
    Icon: <ContactIcon />,
  },
  qualityIncidents: {
    title: TAB_NAME_TITLES.QUALITY_INCIDENTS,
    href: '/quality-incidents',
    Icon: <ContactIcon />,
  },
  auditLogs: {
    title: TAB_NAME_TITLES.AUDIT_LOGS,
    href: '/audit-logs',
    Icon: auditLogsIcon(25),
    tabConfigs: [
      PERMISSION_TITLES.NOTES,
      PERMISSION_TITLES.MODIFICATION_HISTORY,
      PERMISSION_TITLES.RECORD_VIEW_HISTORY,
    ],
  },
});
const ridesLightOrder: Array<keyof typeof ridesItems> = [
  'rideDetailLight',
  'standingOrderDetail',
  'contacts',
  'auditLogs',
];
const ridesFullOrder: Array<keyof typeof ridesItems> = [
  'rideInfoFull',
  'standingOrderDetail',
  'contacts',
  'qualityIncidents',
  'auditLogs',
];

/*** =========================== MONITORING ==================================== ***/

const monitoringItems = item({
  board: {
    title: TAB_NAME_TITLES.MONITORING_BOARD,
    href: '/',
    Icon: <DataTableIcon />,
  },
});
const monitoringOrder: Array<keyof typeof monitoringItems> = ['board'];

/*** =========================== ASSIGNMENT ==================================== ***/

const assignmentItems = item({
  boardLight: {
    title: TAB_NAME_TITLES.ASSIGNMENT_BOARD,
    href: '/information',
    Icon: <DataTableIcon />,
    name: 'info',
  },
  infoFull: {
    title: TAB_NAME_TITLES.INFORMATION,
    href: '/information',
    Icon: <BuildingOutlineIcon />,
    name: 'info',
  },
});
const assignmentLightOrder: Array<keyof typeof assignmentItems> = [
  'boardLight',
];
const assignmentFullOrder: Array<keyof typeof assignmentItems> = ['infoFull'];

/*** ============================= CONTRACTORS =================================== ***/

const contractorsItems = item({
  infoLight: {
    title: TAB_NAME_TITLES.INFORMATION,
    href: '/information',
    Icon: <BuildingOutlineIcon />,
    name: 'info',
  },
  infoFull: {
    title: TAB_NAME_TITLES.INFORMATION,
    href: '/information',
    Icon: <BuildingOutlineIcon />,
    module: 'Information',
  },
  contacts: {
    title: TAB_NAME_TITLES.CONTACTS,
    href: '/contacts',
    Icon: <ContactIcon />,
  },
  agreements: {
    title: TAB_NAME_TITLES.AGREEMENTS,
    href: '/agreements',
    Icon: <HandshakeHeartIcon />,
  },
  services: {
    title: TAB_NAME_TITLES.SERVICES,
    href: '/services',
    Icon: <ServicesIcon />,
  },
  operations: {
    title: TAB_NAME_TITLES.OPERATIONS,
    href: '/operations',
    Icon: <OperationsIcon />,
  },
  compliance: {
    title: TAB_NAME_TITLES.COMPLIANCE,
    href: '/compliance',
    Icon: <ComplianceIcon />,
  },
  vehicles: {
    title: TAB_NAME_TITLES.VEHICLES,
    href: '/vehicles',
    Icon: <MinivanIcon />,
  },
  personnel: {
    title: TAB_NAME_TITLES.PERSONNEL,
    href: '/personnel',
    Icon: <PersonalIcon />,
  },
  documents: {
    title: TAB_NAME_TITLES.DOCUMENTS,
    href: '/documents',
    Icon: <DocsIcon />,
  },
  access: {
    title: TAB_NAME_TITLES.ACCESS,
    href: '/access',
    Icon: <UsersIcon />,
  },
  rides: {
    title: TAB_NAME_TITLES.RIDES,
    href: '/rides',
    Icon: <CarOutlineIcon />,
  },
  auditLogs: {
    title: TAB_NAME_TITLES.AUDIT_LOGS,
    href: '/audit-logs',
    Icon: auditLogsIcon(25),
    tabConfigs: [
      PERMISSION_TITLES.NOTES,
      PERMISSION_TITLES.MODIFICATION_HISTORY,
      PERMISSION_TITLES.RECORD_VIEW_HISTORY,
    ],
  },
});
const contractorsLightOrder: Array<keyof typeof contractorsItems> = [
  'infoLight',
  'operations',
  'rides',
  'auditLogs',
];
const contractorsFullOrder: Array<keyof typeof contractorsItems> = [
  'infoFull',
  'contacts',
  'agreements',
  'services',
  'operations',
  'compliance',
  'vehicles',
  'personnel',
  'documents',
  'access',
  'rides',
  'auditLogs',
];
/*** ============================= CONTRACTOR PORTAL =================================== ***/

const contractorPortalItems = item({
  home: {
    title: TAB_NAME_TITLES.HOME,
    href: '/',
    Icon: <LayoutIcon />,
    name: 'home',
  },
  admin: {
    title: TAB_NAME_TITLES.ADMIN,
    href: '/admin/users',
    Icon: <UsersIcon />,
    children: [
      {
        id: 1,
        title: TAB_NAME_TITLES.USERS,
        href: '/admin/users',
        Icon: <PersonalIcon />,
      },
      {
        id: 2,
        title: TAB_NAME_TITLES.PAYER_INVITATIONS,
        href: '/admin/payer-invitations',
        Icon: <MailIcon />,
      },
      {
        id: 3,
        title: TAB_NAME_TITLES.PERMISSIONS,
        href: '/admin/permissions',
        Icon: <ServicesIcon />,
      },
    ],
  },
  contractor: {
    title: TAB_NAME_TITLES.CONTRACTOR,
    href: '/contractors/information',
    Icon: <CompanyIcon />,
    children: [
      {
        id: 1,
        title: TAB_NAME_TITLES.INFORMATION,
        href: '/contractors/information',
        Icon: <PortalInfoIcon />,
      },
      {
        id: 3,
        title: TAB_NAME_TITLES.STANDING_ORDERS,
        href: '/contractors/standing-orders',
        Icon: <OrderOutlineIcon />,
      },
      {
        id: 2,
        title: TAB_NAME_TITLES.AUDIT_LOGS,
        href: '/contractors/audit-logs',
        Icon: auditLogsIcon(20),
      },
      {
        id: 3,
        title: TAB_NAME_TITLES.NOTIFICATIONS,
        href: '/contractors/notifications',
        Icon: <ContactIcon />,
      },
    ],
  },
  credentialing: {
    title: 'Credentialing',
    href: '/credentialing/contractor',
    Icon: <CredentialingIcon width={25} height={25} />,
    children: [
      {
        id: 1,
        title: 'Contractor',
        href: '/credentialing/contractor',
        Icon: <CompanyIcon />,
      },
      {
        id: 2,
        title: TAB_NAME_TITLES.PERSONNEL,
        href: '/credentialing/personnel',
        Icon: <UserIcon />,
      },
      {
        id: 3,
        title: 'Vehicles',
        href: '/credentialing/vehicles',
        Icon: <CarOutlineIcon />,
      },
    ],
  },
  assignment: {
    title: TAB_NAME_TITLES.ASSIGNMENT_SCREEN,
    href: '/assignment-screen',
    Icon: <DataTableIcon />,
    tabConfigs: [
      PERMISSION_TITLES.ASSIGNMENT,
      PERMISSION_TITLES.NOTES,
      PERMISSION_TITLES.MODIFICATION_HISTORY,
      PERMISSION_TITLES.RECORD_VIEW_HISTORY,
    ],
  },
  driverMap: {
    title: TAB_NAME_TITLES.DRIVER_MAP,
    href: '/driver-map',
    Icon: <MapIcon />,
    name: 'driverMap',
  },
  userProfile: {
    title: TAB_NAME_TITLES.USER_PROFILE,
    href: '/user-profile',
    Icon: <UsersIcon />,
    children: [
      {
        id: 1,
        title: TAB_NAME_TITLES.PERSONAL_INFORMATION,
        href: '/user-profile',
        Icon: <UsersIcon />,
      },
      {
        id: 3,
        title: TAB_NAME_TITLES.AUDIT_LOGS,
        href: '/audit-logs',
        Icon: auditLogsIcon(25),
      },
      {
        id: 2,
        title: TAB_NAME_TITLES.RECENT_ACTIVITY,
        href: '/recent-activity',
        Icon: <ListIcon />,
      },
      {
        id: 3,
        title: TAB_NAME_TITLES.PERMISSIONS,
        href: '/permissions',
        Icon: <ServicesIcon />,
      },
    ],
  },
});

const contractorPortalOrder: Array<keyof typeof contractorPortalItems> = [
  'home',
  'admin',
  'contractor',
  'credentialing',
  'assignment',
  'driverMap',
  'userProfile',
];

/*** ============================= STANDING ORDER =================================== ***/

const standingOrderItems = item({
  detailsLight: {
    title: TAB_NAME_TITLES.STANDING_ORDER_DETAILS,
    href: '/details',
    module: 'standingOrder',
    Icon: <OrderOutlineIcon />,
    name: 'ride',
  },
  detailsFull: {
    title: TAB_NAME_TITLES.STANDING_ORDER_DETAILS,
    href: '/rides',
    module: 'standingOrder',
    Icon: <OrderOutlineIcon />,
  },
  contacts: {
    title: TAB_NAME_TITLES.CONTACTS,
    href: '/contacts',
    Icon: <ContactIcon />,
  },
  auditLogs: {
    title: TAB_NAME_TITLES.AUDIT_LOGS,
    href: '/audit-logs',
    Icon: auditLogsIcon(25),
    tabConfigs: [
      PERMISSION_TITLES.NOTES,
      PERMISSION_TITLES.MODIFICATION_HISTORY,
      PERMISSION_TITLES.RECORD_VIEW_HISTORY,
    ],
  },
});
const standingOrderLightOrder: Array<keyof typeof standingOrderItems> = [
  'detailsLight',
  'contacts',
  'auditLogs',
];
const standingOrderFullOrder: Array<keyof typeof standingOrderItems> = [
  'detailsFull',
  'contacts',
  'auditLogs',
];

/*** ============================= REPORTS =================================== ***/

const reportsItems = item({
  rideReports: {
    title: TAB_NAME_TITLES.RIDE_REPORTS,
    href: '/ride-reports',
    Icon: <CarOutlineIcon />,
  },
  memberReports: {
    title: TAB_NAME_TITLES.MEMBER_REPORTS,
    href: '/member-reports',
    Icon: <MemberReportsIcon />,
  },
  rideshareReports: {
    title: TAB_NAME_TITLES.RIDESHARE_REPORTS,
    href: '/rideshare-reports',
    Icon: <CompanyIcon width={24} height={24} />,
  },
  pendingRidesReport: {
    title: TAB_NAME_TITLES.PENDING_RIDES_REPORT,
    href: '/contractor-rejected-reports',
    Icon: <ListIcon />,
    tabConfigs: [
      PERMISSION_TITLES.NOTES,
      PERMISSION_TITLES.MODIFICATION_HISTORY,
      PERMISSION_TITLES.RECORD_VIEW_HISTORY,
    ],
  },
});
const reportsLightOrder: Array<keyof typeof reportsItems> = [
  'rideReports',
  'memberReports',
  'rideshareReports',
  'pendingRidesReport',
];

const reportsFullOrder: Array<keyof typeof reportsItems> = [
  'rideReports',
  'memberReports',
  'rideshareReports',
  'pendingRidesReport',
];

export const getHomeNavigationData = (
  userType: string,
  contractorName = 'Contractor',
  permissions: Array<{ name: string; abilities?: Array<{ name: string }> }> = []
): Array<navigationItemType> => {
  if (userType !== CONTRACTOR_USER) return buildSection(homeItems, homeOrder);

  const hasAssignmentPermission = permissions.some(
    ({ name }) => name === PERMISSION_TITLES.ASSIGNMENT
  );

  const canReadUsers = permissions.some(
    ({ name, abilities }) =>
      name === PERMISSION_TITLES.USERS &&
      abilities?.some((ability) => ability.name === 'Read')
  );

  // a Dispatcher reads Personnel only (no Edit), and the Contractor sub-tab
  // is hidden from them, so Credentialing opens on Personnel
  const personnelPermission = permissions.find(
    ({ name }) => name === PERMISSION_TITLES.DRIVERS
  );
  const isPersonnelReadOnly =
    !!personnelPermission &&
    !personnelPermission.abilities?.some((ability) => ability.name === 'Edit');

  return buildSection(contractorPortalItems, contractorPortalOrder)
    .filter(
      (item) => item.href !== '/assignment-screen' || hasAssignmentPermission
    )
    .filter((item) => item.title !== TAB_NAME_TITLES.ADMIN || canReadUsers)
    .map((item) =>
      item.href === '/contractors/information'
        ? { ...item, title: contractorName }
        : item
    )
    .map((item) =>
      item.href === '/credentialing/contractor' && isPersonnelReadOnly
        ? {
            ...item,
            href: '/credentialing/personnel',
            children: item.children?.filter(
              ({ href }) => href !== '/credentialing/contractor'
            ),
          }
        : item
    );
};

const lightNavigationData: navigationDataType = {
  payorsNavigationData: buildSection(payorsItems, payorsLightOrder),
  membersNavigationData: buildSection(membersItems, membersLightOrder),
  adminNavigationData: buildSection(adminItems, adminLightOrder),
  placesNavigationData: buildSection(placesItems, placesLightOrder),
  ridesNavigationData: buildSection(ridesItems, ridesLightOrder),
  monitoringNavigationData: buildSection(monitoringItems, monitoringOrder),
  assignmentNavigationData: buildSection(assignmentItems, assignmentLightOrder),
  contractorsNavigationData: buildSection(
    contractorsItems,
    contractorsLightOrder
  ),
  standingOrderNavigationData: buildSection(
    standingOrderItems,
    standingOrderLightOrder
  ),
  reportsNavigationData: buildSection(reportsItems, reportsLightOrder),
  profileNavigationData: buildSection(profileItems, profileOrder),
};

const fullNavigationData: navigationDataType = {
  payorsNavigationData: buildSection(payorsItems, payorsFullOrder),
  membersNavigationData: buildSection(membersItems, membersFullOrder),
  adminNavigationData: buildSection(adminItems, adminFullOrder),
  placesNavigationData: buildSection(placesItems, placesFullOrder),
  ridesNavigationData: buildSection(ridesItems, ridesFullOrder),
  monitoringNavigationData: buildSection(monitoringItems, monitoringOrder),
  assignmentNavigationData: buildSection(assignmentItems, assignmentFullOrder),
  contractorsNavigationData: buildSection(
    contractorsItems,
    contractorsFullOrder
  ),
  standingOrderNavigationData: buildSection(
    standingOrderItems,
    standingOrderFullOrder
  ),
  reportsNavigationData: buildSection(reportsItems, reportsFullOrder),
  profileNavigationData: buildSection(profileItems, profileOrder),
};

export const data: navigationDataType =
  APP_VERSION === 'full' ? fullNavigationData : lightNavigationData;
